import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { usePrizes, useDeletePrize, usePublishPrize, useUnpublishPrize } from '../../services/gamification';
import { useImageUrl } from '../../services/uploads';
import type { PrizeCategory, Prize } from '../../types/gamification';
import { useDebounce } from '../../hooks/useDebounce';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/hooks/useAuth';
import { planSupportsGamification } from '@/config/navigation';
import FeatureUpgradeNotice from '@/components/common/FeatureUpgradeNotice';
import {
  PlusIcon,
  GiftIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PrizeSearchField, PrizeFiltersButton, type PrizeGender } from '../../components/prizes/PrizeFilters';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25] as const;

const CATEGORY_OPTIONS: { value: 'all' | PrizeCategory; label: string }[] = [
  { value: 'all', label: 'All Categories' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'gift_card', label: 'Gift Card' },
  { value: 'experience', label: 'Experience' },
  { value: 'digital', label: 'Digital' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'custom', label: 'Custom' },
];

const PRIZE_TABS: { value: 'all' | 'published' | 'draft'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
];

const tabBadgeColors: Record<string, string> = {
  All: 'bg-grey-800 text-white dark:bg-grey-200 dark:text-grey-900',
  Published: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  Draft: 'bg-grey-100 text-grey-600 dark:bg-grey-800 dark:text-grey-400',
};

function formatCategory(cat: PrizeCategory): string {
  return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return { date, time };
}

function ResolvedImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [resolved, isLoading] = useImageUrl(src);
  if (isLoading) {
    return <div className={`${className ?? ''} animate-pulse bg-grey-500/20`} />;
  }
  if (!resolved) return null;
  return <img src={resolved} alt={alt} className={className} />;
}

function RowActionsMenu({
  prize,
  onEdit,
  onTogglePublish,
  onDelete,
}: {
  prize: Prize;
  onEdit: () => void;
  onTogglePublish: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="flex items-center justify-center p-[5px] rounded-full text-grey-600 bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-grey-500/16"
        aria-label="Row actions"
      >
        <EllipsisVerticalIcon className="w-5 h-5" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-30 w-44 rounded-2xl border border-grey-500/20 bg-white dark:bg-grey-900 shadow-[0px_12px_24px_-4px_rgba(189,188,201,0.12),0px_0px_2px_0px_rgba(189,188,201,0.2)] dark:shadow-[0px_12px_24px_-4px_rgba(38,37,45,0.12),0px_0px_2px_0px_rgba(38,37,45,0.2)] py-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onEdit();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-body2 text-text-primary bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-grey-500/8"
          >
            <PencilSquareIcon className="w-4 h-4 shrink-0" />
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onTogglePublish();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-body2 text-text-primary bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-grey-500/8"
          >
            {prize.is_draft ? (
              <>
                <EyeIcon className="w-4 h-4 shrink-0" />
                Publish
              </>
            ) : (
              <>
                <EyeSlashIcon className="w-4 h-4 shrink-0" />
                Unpublish
              </>
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete();
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-body2 text-error-500 bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:bg-error-500/[0.06]"
          >
            <TrashIcon className="w-4 h-4 shrink-0" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function PrizesListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { activeMembership } = useAuth();
  const plan = activeMembership?.tenant?.subscription_plan;
  const hasGamification = planSupportsGamification(plan);

  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const searchParam = searchParams.get('search') || '';
  const statusParam = searchParams.get('status') as 'published' | 'draft' | null;
  // Client-side facets from the shared Filters panel (same as the learner shop).
  const [genderFilter, setGenderFilter] = useState<PrizeGender[]>([]);
  const [colorFilter, setColorFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [pointsRange, setPointsRange] = useState<[number, number] | null>(null);

  const [searchInput, setSearchInput] = useState(searchParam);
  const [rowsPerPage, setRowsPerPage] = useState<5 | 10 | 25>(5);
  const [dense, setDense] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
      params.set('page', '1');
    } else {
      params.delete('search');
    }
    setSearchParams(params, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const queryParams = useMemo(
    () => ({
      page: pageParam,
      per_page: rowsPerPage,
      search: debouncedSearch || undefined,
      is_draft: statusParam === 'draft' ? true : statusParam === 'published' ? false : undefined,
    }),
    [pageParam, rowsPerPage, debouncedSearch, statusParam]
  );

  const { data, isLoading, error } = usePrizes(queryParams);
  const deleteMutation = useDeletePrize();
  const publishMutation = usePublishPrize();
  const unpublishMutation = useUnpublishPrize();

  const prizes = data?.prizes ?? [];
  // Panel facets filter the loaded page client-side, like the learner shop.
  const visiblePrizes = prizes.filter((p) => {
    if (genderFilter.length && !genderFilter.includes((p.gender ?? 'unisex') as PrizeGender))
      return false;
    if (colorFilter.length && !(p.colors ?? []).some((c) => colorFilter.includes(c.toLowerCase())))
      return false;
    if (categoryFilter.length && !categoryFilter.includes(p.category)) return false;
    if (pointsRange && (p.price_points < pointsRange[0] || p.price_points > pointsRange[1]))
      return false;
    return true;
  });
  const pagination = data?.pagination;
  const totalItems = pagination?.total ?? 0;
  const totalPages = pagination?.pages ?? 1;

  const rangeStart = totalItems === 0 ? 0 : (pageParam - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(pageParam * rowsPerPage, totalItems);

  const handleFilterChange = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === 'all' || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleRowsPerPageChange = (val: number) => {
    setRowsPerPage(val as 5 | 10 | 25);
    const params = new URLSearchParams(searchParams);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams({});
  };

  const handleDelete = async (prize: Prize) => {
    if (!window.confirm(`Delete "${prize.title}"? This cannot be undone.`)) return;
    try {
      await deleteMutation.mutateAsync(prize.id);
      toast.success('Prize deleted');
    } catch {
      toast.error('Failed to delete prize');
    }
  };

  const handleTogglePublish = async (prize: Prize) => {
    try {
      if (prize.is_draft) {
        await publishMutation.mutateAsync(prize.id);
        toast.success('Prize published');
      } else {
        await unpublishMutation.mutateAsync(prize.id);
        toast.success('Prize unpublished');
      }
    } catch {
      toast.error(prize.is_draft ? 'Failed to publish' : 'Failed to unpublish');
    }
  };

  const hasActiveFilters = !!(debouncedSearch || statusParam);
  const avatarSize = dense ? 'w-10 h-10' : 'w-16 h-16';

  // Prize management is a Pro/Enterprise feature. On lower tiers the nav entry
  // still shows, but the page surfaces an upgrade prompt instead of the
  // catalogue + "Add Prize" actions (the backend also rejects prize writes).
  if (!hasGamification) {
    return (
      <motion.div {...slideUp} className="min-h-screen">
        <div>
          <PageHeader title="Prize Management" className="mb-6" />
          <FeatureUpgradeNotice
            feature="Prize Management"
            description="Reward learners with prizes they can redeem using their points."
            currentPlan={plan}
            icon={<GiftIcon className="w-8 h-8" />}
          />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div {...slideUp} className="min-h-screen">
      <div>
        <PageHeader
          title="Prize Management"
          className="mb-6"
          trail={statusParam ? [{ label: 'Prize Management', to: '/prizes' }] : undefined}
          leaf={statusParam ? { published: 'Published', draft: 'Drafts' }[statusParam] : undefined}
          actions={
            <button
              onClick={() => navigate('/prizes/new')}
              className="flex items-center gap-2 px-5 py-2.5 text-subtitle2 bg-primary-500 rounded-full border-none cursor-pointer shadow-[0px_8px_16px_rgb(var(--color-primary) / 0.24)] dark:shadow-[0px_8px_16px_rgba(156,124,232,0.2)] transition-colors duration-150 hover:bg-primary-700 dark:hover:bg-primary-400 active:scale-[0.97]"
            >
              <PlusIcon className="w-4 h-4 shrink-0" />
              Add Prize
            </button>
          }
        />

        {/* Table card */}
        <div className="flex flex-col items-start overflow-hidden rounded-3xl shadow-[0px_0px_2px_0px_rgba(189,188,201,0.2),0px_12px_24px_-4px_rgba(189,188,201,0.12)] dark:shadow-[0px_0px_2px_0px_rgba(38,37,45,0.2),0px_12px_24px_-4px_rgba(38,37,45,0.12)] w-full relative">
          {/* Background paper + gradient overlay (Figma: bg paper + image at 40% opacity) */}
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none rounded-3xl">
            <div className="absolute inset-0 rounded-3xl bg-surface-primary" />
            <div className="brand-gradient absolute inset-0 opacity-40 rounded-3xl" />
          </div>

          {/* Content layer */}
          <div className="relative z-[1] w-full flex flex-col">
            {/* Status Tabs — same strip as the Learners list */}
            <div className="border-b border-border-primary px-4 bg-surface-primary w-full">
              <div className="flex">
                {PRIZE_TABS.map((tab) => (
                  <button
                    key={tab.label}
                    onClick={() => handleFilterChange('status', tab.value)}
                    className={`flex items-center gap-2 px-4 py-3.5 body-small font-medium border-b-2 transition-colors ${
                      (statusParam ?? 'all') === tab.value
                        ? 'border-primary-600 text-primary-600 dark:border-primary-350 dark:text-primary-350'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {tab.label}
                    {(statusParam ?? 'all') === tab.value && !isLoading && (
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-semibold ${tabBadgeColors[tab.label]}`}
                      >
                        {totalItems}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            {/* Search + Filters — the exact same pair as the learner prize
                shop; Category lives inside the Filters panel. */}
            <div className="bg-surface-primary w-full p-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <PrizeSearchField value={searchInput} onChange={setSearchInput} />
                <PrizeFiltersButton
                  gender={genderFilter}
                  onGender={setGenderFilter}
                  color={colorFilter}
                  onColor={setColorFilter}
                  colors={Array.from(
                    new Set(prizes.flatMap((p) => (p.colors ?? []).map((c) => c.toLowerCase())))
                  )}
                  scoreRange={pointsRange}
                  onScoreRange={setPointsRange}
                  maxScore={Math.max(200, ...prizes.map((p) => p.price_points))}
                  categories={CATEGORY_OPTIONS.filter((o) => o.value !== 'all')}
                  category={categoryFilter}
                  onCategory={setCategoryFilter}
                />
              </div>
            </div>

            {/* Active filters bar */}
            {hasActiveFilters && (
              <div className="flex flex-col gap-3 items-start pb-4 px-5 w-full">
                <p className="text-body2 text-text-primary m-0">
                  <span className="font-semibold">{totalItems}</span>
                  <span className="text-text-secondary"> results found</span>
                </p>

                <div className="flex gap-2 items-center flex-wrap">
                  {statusParam && (
                    <div className="flex gap-2 items-center p-2 rounded-xl border border-dashed border-grey-500/20">
                      <span className="text-subtitle2 text-text-primary whitespace-nowrap">
                        Status :
                      </span>
                      <div className="flex items-center h-6 rounded-xl pl-[3px] pr-[5px] bg-grey-500/16">
                        <span className="px-[5px] text-[13px] leading-[18px] text-text-primary font-medium whitespace-nowrap">
                          {statusParam.charAt(0).toUpperCase() + statusParam.slice(1)}
                        </span>
                        <button
                          onClick={() => handleFilterChange('status', 'all')}
                          className="flex items-center justify-center w-4 h-4 text-text-primary bg-transparent border-none cursor-pointer opacity-[0.48] p-0"
                          aria-label="Remove status filter"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {debouncedSearch && (
                    <div className="flex gap-2 items-center p-2 rounded-xl border border-dashed border-grey-500/20">
                      <span className="text-subtitle2 text-text-primary whitespace-nowrap">
                        Search :
                      </span>
                      <div className="flex items-center h-6 rounded-xl pl-[3px] pr-[5px] bg-grey-500/16">
                        <span className="px-[5px] text-[13px] leading-[18px] text-text-primary font-medium whitespace-nowrap">
                          {debouncedSearch}
                        </span>
                        <button
                          onClick={() => setSearchInput('')}
                          className="flex items-center justify-center w-4 h-4 text-text-primary bg-transparent border-none cursor-pointer opacity-[0.48] p-0"
                          aria-label="Remove search filter"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-2 justify-center min-h-[36px] min-w-[64px] py-1.5 px-2 rounded-full bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-secondary-500/[0.08]"
                  >
                    <TrashIcon className="w-5 h-5 text-secondary-500 shrink-0" />
                    <span className="text-body2 font-bold text-secondary-500 whitespace-nowrap">
                      Clear
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Table header */}
            <div className="h-[52px] bg-grey-200 dark:bg-grey-800 flex items-center w-full gap-[33px]">
              <div className="w-[24px] shrink-0" />
              <div className="flex-[1_0_0] min-h-[52px] min-w-[52px] px-2.5 flex items-center">
                <span className="text-subtitle2 text-text-secondary">Product</span>
              </div>
              <div className="w-[206px] shrink-0 min-h-[52px] px-2.5 flex items-center">
                <span className="text-subtitle2 text-text-secondary whitespace-nowrap">Created at</span>
              </div>
              <div className="w-[157px] shrink-0 min-h-[52px] px-2.5 flex items-center">
                <span className="text-subtitle2 text-text-secondary whitespace-nowrap">Points Needed</span>
              </div>
              <div className="w-[120px] shrink-0 min-h-[52px] px-2.5 flex items-center">
                <span className="text-subtitle2 text-text-secondary whitespace-nowrap">Status</span>
              </div>
              <div className="shrink-0 min-h-[52px] min-w-[52px] px-2.5" />
            </div>

            {/* Table body */}
            <div className="flex flex-col w-full">
              <phantom-ui {...phantomProps(theme === 'dark', isLoading)}>
                {/* Placeholder rows shown during loading so phantom-ui can measure DOM */}
                {isLoading
                  ? Array.from({ length: rowsPerPage }).map((_, i) => (
                      <div
                        key={i}
                        className="flex gap-[33px] items-center w-full border-b border-dashed border-grey-500/20 bg-surface-primary"
                      >
                        <div className="flex items-center justify-center min-h-[52px] w-[52px] shrink-0 p-2">
                          <div className="w-5 h-5 rounded" />
                        </div>
                        <div className="flex-[1_0_0] min-h-[52px] px-2.5 flex items-center gap-4">
                          <div className={`${avatarSize} rounded-2xl shrink-0`} />
                          <div className="flex flex-col gap-2">
                            <div className="h-3.5 w-[120px] rounded" />
                            <div className="h-3 w-20 rounded" />
                          </div>
                        </div>
                        <div className="w-[206px] shrink-0 min-h-[52px] px-2.5 flex flex-col justify-center gap-1.5">
                          <div className="h-3.5 w-[90px] rounded" />
                          <div className="h-3 w-[60px] rounded" />
                        </div>
                        <div className="w-[157px] shrink-0 min-h-[52px] px-2.5 flex items-center">
                          <div className="h-3.5 w-[50px] rounded" />
                        </div>
                        <div className="w-[120px] shrink-0 min-h-[52px] px-2.5 flex items-center">
                          <div className="h-6 w-[72px] rounded-lg" />
                        </div>
                        <div className="shrink-0 min-h-[52px] px-2.5 flex items-center justify-end">
                          <div className="w-6 h-6 rounded" />
                        </div>
                      </div>
                    ))
                  : null}

              {/* Error state */}
              {!isLoading && error && (
                <div className="flex justify-center py-16 px-4 text-body2 text-error-500">
                  Failed to load prizes. Please try again.
                </div>
              )}

              {/* Empty state */}
              {!isLoading && !error && prizes.length === 0 && (
                <div className="flex justify-center py-20 px-4">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-grey-500/[0.12] flex items-center justify-center">
                      <GiftIcon className="w-7 h-7 text-text-secondary" />
                    </div>
                    {hasActiveFilters ? (
                      <>
                        <p className="text-subtitle1 text-text-primary m-0">No prizes found</p>
                        <p className="text-body2 text-text-secondary m-0">
                          Try adjusting your search or filters
                        </p>
                        <button
                          onClick={handleClearFilters}
                          className="text-body2 font-medium text-primary-500 bg-transparent border-none cursor-pointer"
                        >
                          Clear filters
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-subtitle1 text-text-primary m-0">No prizes yet</p>
                        <p className="text-body2 text-text-secondary m-0">
                          Create your first reward to motivate learners
                        </p>
                        <button
                          onClick={() => navigate('/prizes/new')}
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-subtitle2 bg-primary-500 rounded-full border-none cursor-pointer"
                        >
                          <PlusIcon className="w-4 h-4 shrink-0" />
                          Add Prize
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Data rows */}
              {!isLoading &&
                !error &&
                visiblePrizes.map((prize) => {
                  const { date, time } = formatDate(prize.created_at);

                  return (
                    <div
                      key={prize.id}
                      onClick={() => navigate(`/prizes/${prize.id}`)}
                      className="flex gap-[33px] items-center w-full border-b border-dashed border-grey-500/20 cursor-pointer transition-colors duration-150 bg-surface-primary hover:bg-grey-500/[0.06]"
                    >
                      <div className="w-[24px] shrink-0" />

                      {/* Product */}
                      <div className={`flex-[1_0_0] flex gap-4 items-center ${dense ? 'h-[52px]' : 'h-24'} min-h-[52px] min-w-[52px] px-2.5`}>
                        <div
                          className={`${avatarSize} rounded-2xl border border-grey-700/20 overflow-hidden bg-grey-500/[0.08] flex items-center justify-center shrink-0 relative`}
                        >
                          <div className="absolute inset-0 rounded-2xl border border-white dark:border-grey-800 z-[1] pointer-events-none" />
                          {prize.media_urls.length > 0 ? (
                            <ResolvedImage
                              src={prize.media_urls[0]}
                              alt={prize.title}
                              className="w-full h-full object-cover rounded-2xl block"
                            />
                          ) : (
                            <GiftIcon className="w-6 h-6 text-text-secondary" />
                          )}
                        </div>
                        <div className="flex flex-col items-start justify-center flex-[1_0_0] min-w-0">
                          <p className="text-body2 text-text-primary truncate w-full m-0">
                            {prize.title}
                          </p>
                          <p className="text-body2 text-grey-500 truncate w-full m-0">
                            {formatCategory(prize.category)}
                          </p>
                        </div>
                      </div>

                      {/* Created at */}
                      <div className="w-[206px] shrink-0 flex gap-4 items-center min-h-[52px] px-2.5">
                        <div className="flex-[1_0_0] flex flex-col items-start justify-center min-w-0">
                          <p className="text-body2 text-text-primary truncate w-full m-0">
                            {date}
                          </p>
                          <p className="text-caption text-text-secondary m-0 pt-1">
                            {time}
                          </p>
                        </div>
                      </div>

                      {/* Points Needed */}
                      <div className="w-[157px] shrink-0 flex gap-4 items-center justify-center min-h-[52px] px-2.5">
                        <div className="flex-[1_0_0] flex flex-col h-[22px] items-end justify-between min-w-0">
                          <p className="text-body2 text-text-primary truncate w-full m-0">
                            {prize.price_points.toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Publish badge */}
                      <div className="w-[120px] shrink-0 flex items-center min-h-[52px] px-2.5">
                        {prize.is_draft ? (
                          <span className="inline-flex items-center justify-center h-6 px-1.5 rounded-lg text-overline text-grey-600 bg-grey-500/16 whitespace-nowrap">
                            Draft
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center h-6 px-1.5 rounded-lg text-overline text-success-700 bg-success-500/16 whitespace-nowrap">
                            Published
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="flex gap-2 items-center justify-end min-h-[52px] min-w-[52px] px-2.5 shrink-0"
                      >
                        <RowActionsMenu
                          prize={prize}
                          onEdit={() => navigate(`/prizes/${prize.id}`)}
                          onTogglePublish={() => handleTogglePublish(prize)}
                          onDelete={() => handleDelete(prize)}
                        />
                      </div>
                    </div>
                  );
                })}
              </phantom-ui>
            </div>

            {/* Pagination footer */}
            <div className="flex items-center justify-end gap-5 w-full pl-4 pr-2 py-2.5 overflow-hidden">
              {/* Dense toggle */}
              <div className="flex items-center gap-[9px] shrink-0 mr-auto">
                <button
                  role="switch"
                  aria-checked={dense}
                  onClick={() => setDense((v) => !v)}
                  className={`relative inline-flex w-[33px] h-5 shrink-0 rounded-full border-2 border-transparent cursor-pointer transition-colors duration-200 outline-none ${
                    dense ? 'bg-primary-500' : 'bg-grey-500/60'
                  }`}
                >
                  <span
                    className={`inline-block w-4 h-4 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.2)] transition-transform duration-200 ${
                      dense ? 'translate-x-[13px]' : 'translate-x-0'
                    }`}
                  />
                </button>
                <p className="text-body2 text-text-primary m-0 whitespace-nowrap">Dense</p>
              </div>

              {/* Rows per page */}
              <p className="flex-[1_0_0] text-body2 text-text-primary m-0 text-right whitespace-nowrap min-w-0">
                Rows per page:
              </p>

              <div className="flex gap-1 items-center shrink-0 relative">
                <div className="relative flex items-center">
                  <select
                    value={rowsPerPage}
                    onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                    className="appearance-none bg-transparent text-body2 text-text-primary outline-none cursor-pointer pl-0 pr-5 border-none"
                  >
                    {ROWS_PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-text-primary pointer-events-none" />
                </div>
              </div>

              {/* Range */}
              <p className="text-body2 text-text-primary m-0 whitespace-nowrap shrink-0">
                {rangeStart}–{rangeEnd} of {totalItems}
              </p>

              {/* Prev / Next */}
              <div className="flex items-start shrink-0">
                <button
                  onClick={() => handlePageChange(pageParam - 1)}
                  disabled={pageParam <= 1}
                  aria-label="Previous page"
                  className={`flex items-center justify-center p-2 rounded-full border-none bg-transparent transition-colors duration-150 ${
                    pageParam <= 1
                      ? 'text-grey-500/80 cursor-default pointer-events-none'
                      : 'text-text-primary cursor-pointer hover:bg-grey-500/[0.12]'
                  }`}
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handlePageChange(pageParam + 1)}
                  disabled={pageParam >= totalPages}
                  aria-label="Next page"
                  className={`flex items-center justify-center p-2 rounded-full border-none bg-transparent transition-colors duration-150 ${
                    pageParam >= totalPages
                      ? 'text-grey-500/80 cursor-default pointer-events-none'
                      : 'text-text-primary cursor-pointer hover:bg-grey-500/[0.12]'
                  }`}
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
